import React, { useState } from "react";
import {
  FaMoneyBillWave,
  FaCreditCard,
  FaQrcode,
  FaTimes,
  FaCheck,
} from "react-icons/fa";

const CODPaymentModal = ({
  isOpen,
  onClose,
  onConfirm,
  bookingDetails,
  totalAmount,
  isSubmitting = false,
}) => {
  const [paymentMethod, setPaymentMethod] = useState("cash");

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm({
      method: paymentMethod,
      amount: totalAmount,
      bookingId: bookingDetails._id,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800">Collect Payment</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isSubmitting}
            >
              <FaTimes size={20} />
            </button>
          </div>

          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-blue-800 mb-2">
                Booking Details
              </h3>
              <div className="text-sm text-blue-700 space-y-3">
                <p>
                  <span className="font-medium">Booking ID:</span>{" "}
                  {bookingDetails.bookingId}
                </p>
                <div>
                  <p className="font-medium">📍 Pickup Location:</p>
                  <p className="ml-4 font-medium">
                    {bookingDetails.pickupLocation?.name || bookingDetails.from || "Unknown Location"}
                  </p>
                  {(bookingDetails.pickupAddress || bookingDetails.pickupLocation?.address) && (
                    <p className="ml-4 text-blue-600 text-xs">
                      {bookingDetails.pickupAddress || bookingDetails.pickupLocation?.address}
                    </p>
                  )}
                </div>
                <div>
                  <p className="font-medium">📍 Drop-off Location:</p>
                  <p className="ml-4 font-medium">
                    {bookingDetails.dropLocation?.name || bookingDetails.to || "Unknown Location"}
                  </p>
                  {(bookingDetails.dropAddress || bookingDetails.dropLocation?.address) && (
                    <p className="ml-4 text-blue-600 text-xs">
                      {bookingDetails.dropAddress || bookingDetails.dropLocation?.address}
                    </p>
                  )}
                </div>
                <p>
                  <span className="font-medium">Amount:</span> ₹{totalAmount}
                </p>
              </div>
            </div>

            <h3 className="text-lg font-semibold mb-4">
              How did the passenger pay?
            </h3>

            <div className="space-y-3">
              <div
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  paymentMethod === "cash"
                    ? "border-green-500 bg-green-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => setPaymentMethod("cash")}
              >
                <div className="flex items-center">
                  <FaMoneyBillWave className="text-green-500 mr-3" size={20} />
                  <div>
                    <h4 className="font-medium">Cash Payment</h4>
                    <p className="text-sm text-gray-600">
                      Passenger paid in cash
                    </p>
                  </div>
                  {paymentMethod === "cash" && (
                    <FaCheck className="text-green-500 ml-auto" size={16} />
                  )}
                </div>
              </div>

              <div
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  paymentMethod === "online"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => setPaymentMethod("online")}
              >
                <div className="flex items-center">
                  <FaCreditCard className="text-blue-500 mr-3" size={20} />
                  <div>
                    <h4 className="font-medium">Digital Payment</h4>
                    <p className="text-sm text-gray-600">
                      UPI, Card, or Net Banking
                    </p>
                  </div>
                  {paymentMethod === "online" && (
                    <FaCheck className="text-blue-500 ml-auto" size={16} />
                  )}
                </div>
              </div>

              <div
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  paymentMethod === "qr"
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => setPaymentMethod("qr")}
              >
                <div className="flex items-center">
                  <FaQrcode className="text-purple-500 mr-3" size={20} />
                  <div>
                    <h4 className="font-medium">QR Code Payment</h4>
                    <p className="text-sm text-gray-600">
                      Scanner or QR-based payment
                    </p>
                  </div>
                  {paymentMethod === "qr" && (
                    <FaCheck className="text-purple-500 ml-auto" size={16} />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Confirming..." : "Confirm Payment"}
            </button>
          </div>

          {paymentMethod && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                ⚠️ By confirming, you acknowledge that you have received ₹
                {totalAmount} from the passenger.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CODPaymentModal;
